import { useEffect, useState } from "react";
import { useOrganizationHasComputeResourcesQuery } from "server/devplane/organizations/queries";

interface UseOrganizationHasComputeResourcesOptions {
  organizationId: string;
  currentQueryHasResources: boolean;
  isCurrentQueryUnfiltered: boolean;
  isInitialPageLoading: boolean;
}

export function useOrganizationHasComputeResources({
  organizationId,
  currentQueryHasResources,
  isCurrentQueryUnfiltered,
  isInitialPageLoading,
}: UseOrganizationHasComputeResourcesOptions) {
  const [organizationsKnownToHaveCompute, setOrganizationsKnownToHaveCompute] =
    useState<ReadonlySet<string>>(() => new Set());
  const [
    organizationsWithLoadedInitialPage,
    setOrganizationsWithLoadedInitialPage,
  ] = useState<ReadonlySet<string>>(() => new Set());
  const existenceQuery =
    useOrganizationHasComputeResourcesQuery(organizationId);
  const currentResponseConfirmsCompute =
    currentQueryHasResources || existenceQuery.data === true;

  useEffect(() => {
    if (currentResponseConfirmsCompute) {
      setOrganizationsKnownToHaveCompute((knownOrganizations) => {
        if (knownOrganizations.has(organizationId)) return knownOrganizations;
        return new Set(knownOrganizations).add(organizationId);
      });
    }
  }, [currentResponseConfirmsCompute, organizationId]);

  useEffect(() => {
    if (!isInitialPageLoading) {
      setOrganizationsWithLoadedInitialPage((loadedOrganizations) => {
        if (loadedOrganizations.has(organizationId)) return loadedOrganizations;
        return new Set(loadedOrganizations).add(organizationId);
      });
    }
  }, [isInitialPageLoading, organizationId]);

  const hasLoadedInitialPage =
    !isInitialPageLoading ||
    organizationsWithLoadedInitialPage.has(organizationId);
  const currentUnfilteredQueryConfirmsNoCompute =
    isCurrentQueryUnfiltered &&
    hasLoadedInitialPage &&
    !currentQueryHasResources;

  return {
    hasComputeResources: currentUnfilteredQueryConfirmsNoCompute
      ? false
      : currentResponseConfirmsCompute ||
        organizationsKnownToHaveCompute.has(organizationId),
    hasLoadedInitialPage,
  };
}
